import { useState, useEffect, useMemo } from 'react';
import { isSuggestionProposalPending } from '../../utils/navCounts';
import api from '../../hooks/useApi';
import Modal from '../shared/Modal';
import Markdown from '../shared/Markdown';
import { fmtDateOnly, fmtDateTime } from '../../utils/dates.js';
import { suggestionStatusTint } from '../shared/floatingWindow';

const STATUS_OPTIONS = [
  { value: 'all',         label: 'All' },
  { value: 'live',        label: 'Live' },
  { value: 'open',        label: 'Open' },
  { value: 'in-progress', label: 'In Progress' },
  { value: 'done',        label: 'Done' },
  { value: 'declined',    label: 'Declined' },
];

// "Live" is a filter, not a status: everything still being worked on.
const LIVE_STATUSES = ['open', 'in-progress'];
// The filters that are not a status a suggestion can be set to.
const FILTER_ONLY = ['all', 'live'];

const PRIORITY_OPTIONS = ['low', 'medium', 'high'];

const STATUS_CLS = {
  open:          'bg-amber-100 text-amber-700 border-amber-200',
  'in-progress': 'bg-blue-100  text-blue-700  border-blue-200',
  done:          'bg-green-100 text-green-700 border-green-200',
  declined:      'bg-red-100   text-red-700   border-red-200',
};

const PRIORITY_CLS = {
  high:   'bg-red-50   text-red-700',
  medium: 'bg-amber-50 text-amber-700',
  low:    'bg-gray-100 text-gray-500',
};

const AI_STATUS_CFG = {
  pending:          { label: 'Pending Review',     cls: 'bg-violet-100 text-violet-700 border-violet-200', dot: 'bg-violet-500' },
  approved:         { label: 'Approved',            cls: 'bg-emerald-100 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
  declined:         { label: 'Declined',            cls: 'bg-red-100 text-red-700 border-red-200', dot: 'bg-red-400' },
  implemented:      { label: 'Implemented',         cls: 'bg-indigo-100 text-indigo-700 border-indigo-200', dot: 'bg-indigo-500' },
  awaiting_testing: { label: 'Awaiting Testing',    cls: 'bg-amber-100 text-amber-700 border-amber-200', dot: 'bg-amber-500' },
};

function StatusBadge({ status }) {
  const cls = STATUS_CLS[status] || STATUS_CLS.open;
  const label = STATUS_OPTIONS.find(o => o.value === status)?.label || status;
  return <span className={`text-[7px] px-1.5 py-0.5 rounded-full border font-bold ${cls}`}>{label}</span>;
}

function PriorityBadge({ priority }) {
  const cls = PRIORITY_CLS[priority] || PRIORITY_CLS.medium;
  return <span className={`text-[7px] px-1.5 py-0.5 rounded font-semibold capitalize ${cls}`}>{priority}</span>;
}

function AiStatusBadge({ status }) {
  if (!status || status === 'none') return null;
  const cfg = AI_STATUS_CFG[status];
  if (!cfg) return null;
  return (
    <span className={`inline-flex items-center gap-1 text-[7px] px-1.5 py-0.5 rounded-full border font-bold ${cfg.cls}`}>
      <span className={`w-1 h-1 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

function daysAgo(dateStr) {
  if (!dateStr) return null;
  return Math.floor((Date.now() - new Date(dateStr)) / (1000 * 60 * 60 * 24));
}

// ── Detail drawer ─────────────────────────────────────────────────────────────

// onSaved refreshes the list AND closes the drawer; onUpdated only refreshes it.
// Sending a notification needs the second: the drawer has to stay open so the
// "Last sent" confirmation is visible, otherwise the window shuts and there is
// no evidence the thing you pressed did anything.
function DetailDrawer({ suggestion, onClose, onSaved, onUpdated, readOnly = false }) {
  const [status, setStatus]         = useState(suggestion.status);
  const [priority, setPriority]     = useState(suggestion.priority);
  const [adminNotes, setAdminNotes] = useState(suggestion.admin_notes || '');
  const [saving, setSaving]         = useState(false);
  const [actioning, setActioning]   = useState(false);
  const [notifying, setNotifying]   = useState(false);
  const [notifiedAt, setNotifiedAt] = useState(suggestion.notes_notified_at || null);
  const [error, setError]           = useState('');
  const [tab, setTab]               = useState(
    suggestion.ai_proposal_status === 'pending' ? 'proposal' : 'details'
  );

  const saveDetails = async () => {
    setSaving(true); setError('');
    try {
      const updated = await api.put(`suggestions/${suggestion.id}`, { status, priority, admin_notes: adminNotes });
      onSaved(updated);
      onClose();
    } catch (e) { setError(e.message || 'Save failed'); setSaving(false); }
  };

  // Save first, then send. The notification carries whatever admin_notes is in
  // the database, so sending against unsaved edits would push out the OLD note
  // and leave the new one on screen looking as though it had gone. Saving here
  // also means the drawer stays open, unlike Save Changes, because the point of
  // this button is to watch it confirm.
  const sendNotification = async () => {
    setNotifying(true); setError('');
    try {
      await api.put(`suggestions/${suggestion.id}`, { status, priority, admin_notes: adminNotes });
      const updated = await api.post(`suggestions/${suggestion.id}/notify`, {});
      setNotifiedAt(updated.notes_notified_at);
      onUpdated(updated);
    } catch (e) { setError(e.message || 'The notification could not be sent'); }
    finally { setNotifying(false); }
  };

  const handleProposalAction = async (action) => {
    setActioning(true); setError('');
    try {
      const updated = await api.put(`suggestions/${suggestion.id}`, { ai_proposal_status: action });
      onSaved(updated);
      onClose();
    } catch (e) { setError(e.message || 'Action failed'); setActioning(false); }
  };

  const aiStatus = suggestion.ai_proposal_status;
  const hasPendingProposal = aiStatus === 'pending';

  return (
    <Modal title={suggestion.title} onClose={onClose}>
        <div className="pb-3 border-b border-gray-100">
          <div className="text-[8px] text-gray-400">
            By {suggestion.raised_by_name} · {fmtDateOnly(suggestion.created_at)}
            {suggestion.category && <> · <span className="text-gray-500">{suggestion.category}</span></>}
          </div>

          <div className="flex gap-0.5 mt-3">
            {[
              { id: 'proposal', label: 'Proposal', show: !!suggestion.ai_proposal },
              { id: 'details',  label: 'Details & Edit',  show: true },
            ].filter(t => t.show).map(t => (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`px-3 py-1 rounded text-[8px] font-semibold transition-colors ${
                  tab === t.id ? 'bg-navy text-white' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}>
                {t.label}
                {t.id === 'proposal' && hasPendingProposal && (
                  <span className="ml-1 inline-block w-1.5 h-1.5 rounded-full bg-violet-500 align-middle" />
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="pt-4">
          {error && <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-[9px] text-red-700">{error}</div>}

          {tab === 'proposal' && suggestion.ai_proposal && (
            <div>
              {suggestion.ai_batch_label && (
                <div className="flex items-center gap-1.5 mb-3">
                  <span className="text-[7px] bg-violet-50 text-violet-600 border border-violet-200 px-2 py-0.5 rounded-full font-semibold">
                    Batch: {suggestion.ai_batch_label}
                  </span>
                </div>
              )}
              <div className="flex items-center gap-2 mb-2">
                <div className="text-[8px] font-bold uppercase tracking-wide text-gray-400">Proposal</div>
                <AiStatusBadge status={aiStatus} />
              </div>
              <div className="bg-violet-50 border border-violet-200 rounded-lg p-3 mb-4">
                <p className="text-[9px] text-gray-700 leading-relaxed whitespace-pre-wrap">{suggestion.ai_proposal}</p>
              </div>
              {suggestion.ai_proposed_at && (
                <div className="text-[7px] text-gray-400 mb-4">
                  Proposed {fmtDateOnly(suggestion.ai_proposed_at)}
                  {suggestion.ai_reviewed_at && ` · Reviewed ${fmtDateOnly(suggestion.ai_reviewed_at)}`}
                </div>
              )}
              {suggestion.ai_implemented_notes && (
                <div className="mb-4">
                  <div className="text-[7px] font-bold uppercase tracking-wide text-gray-400 mb-1">Implementation Notes</div>
                  <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 text-[9px] text-gray-700 leading-relaxed whitespace-pre-wrap">
                    {suggestion.ai_implemented_notes}
                  </div>
                </div>
              )}
              {hasPendingProposal && !readOnly && (
                <div className="flex gap-2 pt-2 border-t border-gray-100 mt-4 sticky bottom-0 -mx-5 -mb-5 px-5 pb-5 bg-white rounded-b-xl">
                  <button onClick={() => handleProposalAction('approved')} disabled={actioning}
                    className="flex-1 py-2 text-[9px] font-bold bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg disabled:opacity-50 transition-colors">
                    {actioning ? '…' : '✓ Approve'}
                  </button>
                  <button onClick={() => handleProposalAction('declined')} disabled={actioning}
                    className="flex-1 py-2 text-[9px] font-bold bg-white border-2 border-red-300 text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50 transition-colors">
                    {actioning ? '…' : '✕ Decline'}
                  </button>
                </div>
              )}
              {aiStatus === 'approved' && (
                <div className="mt-2 p-2.5 bg-emerald-50 border border-emerald-200 rounded-lg text-[8px] text-emerald-700 font-semibold text-center">
                  Approved — awaiting implementation
                </div>
              )}
            </div>
          )}

          {tab === 'details' && (
            <div>
              <Markdown text={suggestion.description} className="text-[9px] text-gray-700 leading-relaxed mb-4 p-3 bg-gray-50 rounded-lg" />
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="block text-[7px] font-bold uppercase tracking-wide text-gray-500 mb-1">Status</label>
                  <select value={status} onChange={e => setStatus(e.target.value)} disabled={readOnly}
                    className="w-full border border-gray-200 rounded px-2 py-1.5 text-[9px] focus:outline-none focus:border-blue-400 bg-white disabled:bg-gray-50 disabled:text-gray-500">
                    {STATUS_OPTIONS.filter(o => !FILTER_ONLY.includes(o.value)).map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[7px] font-bold uppercase tracking-wide text-gray-500 mb-1">Priority</label>
                  <select value={priority} onChange={e => setPriority(e.target.value)} disabled={readOnly}
                    className="w-full border border-gray-200 rounded px-2 py-1.5 text-[9px] focus:outline-none focus:border-blue-400 bg-white disabled:bg-gray-50 disabled:text-gray-500">
                    {PRIORITY_OPTIONS.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                  </select>
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-[7px] font-bold uppercase tracking-wide text-gray-500 mb-1">
                  Admin Notes <span className="text-gray-400 normal-case">(visible to submitter)</span>
                </label>
                <textarea value={adminNotes} onChange={e => setAdminNotes(e.target.value)} disabled={readOnly}
                  placeholder="Provide an update for the person who raised this…"
                  rows={4}
                  className="w-full border border-gray-200 rounded px-2 py-1.5 text-[9px] focus:outline-none focus:border-blue-400 resize-none disabled:bg-gray-50 disabled:text-gray-500" />
              </div>
              {/* A status change notifies on its own. This is for the other
                  case: the note has moved but the status has not, and the
                  submitter would otherwise not know until they next looked. */}
              {!readOnly && (
                <div className="mb-4 p-2.5 bg-gray-50 border border-gray-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <button onClick={sendNotification} disabled={notifying || saving || !adminNotes.trim()}
                      title={!adminNotes.trim() ? 'Write an admin note first — there is nothing to send' : 'Save and notify the submitter of this note'}
                      className="px-3 py-1.5 text-[9px] font-semibold bg-white border border-navy text-navy rounded hover:bg-navy hover:text-white disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-navy disabled:cursor-not-allowed transition-colors shrink-0">
                      {notifying ? 'Sending…' : '📣 Send Notification'}
                    </button>
                    <div className="text-[7px] text-gray-500 leading-snug">
                      Saves this note, then tells {suggestion.raised_by_name} it is there.
                      {notifiedAt && (
                        <div className="text-emerald-600 font-semibold mt-0.5">
                          Last sent {fmtDateTime(notifiedAt)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-3 border-t border-gray-100 mt-4 sticky bottom-0 -mx-4 -mb-3 px-4 pb-3 bg-white">
                <button onClick={onClose} className="px-3 py-1.5 text-[9px] border border-gray-200 rounded hover:bg-gray-50 text-gray-600">Close</button>
                {!readOnly && (
                  <button onClick={saveDetails} disabled={saving || notifying}
                    className="px-3 py-1.5 text-[9px] bg-navy text-white rounded font-semibold hover:bg-navy/90 disabled:opacity-50">
                    {saving ? 'Saving…' : 'Save Changes'}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
    </Modal>
  );
}

// ── Batch card (pending proposals) ────────────────────────────────────────────

function BatchCard({ batchId, batchLabel, items, onAction, actioning, readOnly = false }) {
  const [expanded, setExpanded] = useState(true);

  return (
    <div className="bg-white border border-violet-200 rounded-xl overflow-hidden mb-3">
      <div className="flex items-center justify-between px-4 py-3 bg-violet-50 border-b border-violet-200">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-violet-500" />
          <div>
            <div className="text-[9px] font-bold text-violet-800">{batchLabel || `Batch ${batchId?.slice(0,6)}`}</div>
            <div className="text-[7px] text-violet-500">{items.length} suggestion{items.length !== 1 ? 's' : ''}</div>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {!readOnly && (<>
            <button onClick={() => onAction(items.map(i => i.id), 'approved')} disabled={actioning}
              className="px-3 py-1 text-[8px] font-bold bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg disabled:opacity-50 transition-colors">
              ✓ Approve All
            </button>
            <button onClick={() => onAction(items.map(i => i.id), 'declined')} disabled={actioning}
              className="px-3 py-1 text-[8px] font-bold border-2 border-red-300 text-red-600 hover:bg-red-50 rounded-lg bg-white disabled:opacity-50 transition-colors">
              ✕ Decline All
            </button>
          </>)}
          <button onClick={() => setExpanded(e => !e)} className="text-[8px] text-violet-500 hover:text-violet-700 px-2 py-1">
            {expanded ? '▲' : '▼'}
          </button>
        </div>
      </div>
      {expanded && (
        <div className="divide-y divide-gray-100">
          {items.map(s => (
            <div key={s.id} className="px-4 py-3">
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <div className="text-[9px] font-semibold text-gray-800">{s.title}</div>
                    <PriorityBadge priority={s.priority} />
                  </div>
                  <div className="text-[8px] text-gray-400 mb-1.5">By {s.raised_by_name}</div>
                  <div className="text-[8px] text-gray-500 leading-relaxed mb-2">{s.description}</div>
                  {s.ai_proposal && (
                    <div className="bg-violet-50 rounded-lg p-2.5 border border-violet-100">
                      <div className="text-[7px] font-bold uppercase tracking-wide text-violet-500 mb-1">Proposal</div>
                      <p className="text-[8px] text-gray-700 leading-relaxed whitespace-pre-wrap">{s.ai_proposal}</p>
                    </div>
                  )}
                </div>
                {!readOnly && (
                  <div className="flex flex-col gap-1 shrink-0">
                    <button onClick={() => onAction([s.id], 'approved')} disabled={actioning}
                      className="px-2 py-1 text-[7px] font-bold bg-emerald-500 hover:bg-emerald-600 text-white rounded disabled:opacity-50">✓</button>
                    <button onClick={() => onAction([s.id], 'declined')} disabled={actioning}
                      className="px-2 py-1 text-[7px] font-bold border border-red-300 text-red-600 hover:bg-red-50 rounded bg-white disabled:opacity-50">✕</button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function SuggestionsAdminPage({ user }) {
  const readOnly = !((user?.user_role || '').split(',').map(r => r.trim()).includes('admin'));
  const [suggestions, setSuggestions]     = useState([]);
  const [loading, setLoading]             = useState(true);
  const [statusFilter, setStatusFilter]   = useState('all');
  const [viewMode, setViewMode]           = useState('list');  // 'list' | 'proposals' | 'stats'
  const [editing, setEditing]             = useState(null);
  const [actioning, setActioning]         = useState(false);

  const load = () => {
    setLoading(true);
    api.get('suggestions/all', {})
      .then(d => setSuggestions(d || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleSaved = (updated) => {
    setSuggestions(prev => prev.map(s => s.id === updated.id ? updated : s));
  };

  const handleBatchAction = async (ids, action) => {
    setActioning(true);
    try {
      const results = await Promise.all(ids.map(id => api.put(`suggestions/${id}`, { ai_proposal_status: action })));
      setSuggestions(prev => prev.map(s => {
        const hit = results.find(r => r.id === s.id);
        return hit ? hit : s;
      }));
    } catch (e) { console.error(e); }
    finally { setActioning(false); }
  };

  const filtered = useMemo(() => {
    let list = suggestions;
    if (viewMode === 'proposals') {
      list = list.filter(s => s.ai_proposal_status && s.ai_proposal_status !== 'none');
    } else if (statusFilter === 'live') {
      list = list.filter(s => LIVE_STATUSES.includes(s.status));
    } else if (statusFilter !== 'all') {
      list = list.filter(s => s.status === statusFilter);
    }
    return list;
  }, [suggestions, statusFilter, viewMode]);

  const pendingProposals = suggestions.filter(isSuggestionProposalPending);

  // Proposal sections
  const pendingBatches = useMemo(() => {
    const pending = suggestions.filter(s => s.ai_proposal_status === 'pending');
    const map = new Map();
    for (const s of pending) {
      const key = s.ai_batch_id || `solo_${s.id}`;
      if (!map.has(key)) map.set(key, { batchId: key, batchLabel: s.ai_batch_label || null, items: [] });
      map.get(key).items.push(s);
    }
    return [...map.values()];
  }, [suggestions]);

  const approvedItems   = useMemo(() => suggestions.filter(s => s.ai_proposal_status === 'approved'),          [suggestions]);
  const awaitingItems   = useMemo(() => suggestions.filter(s => s.ai_proposal_status === 'awaiting_testing'),   [suggestions]);
  const implementedItems = useMemo(() => suggestions.filter(s => s.ai_proposal_status === 'implemented').slice(0, 10), [suggestions]);

  // Stats
  const counts = suggestions.reduce((acc, s) => { acc[s.status] = (acc[s.status] || 0) + 1; return acc; }, {});

  const requesterStats = useMemo(() => {
    const map = {};
    for (const s of suggestions) {
      if (!map[s.raised_by_name]) map[s.raised_by_name] = { name: s.raised_by_name, open: 0, 'in-progress': 0, done: 0, declined: 0, total: 0 };
      map[s.raised_by_name][s.status] = (map[s.raised_by_name][s.status] || 0) + 1;
      map[s.raised_by_name].total++;
    }
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [suggestions]);

  const categoryStats = useMemo(() => {
    const map = {};
    for (const s of suggestions) {
      const cat = s.category || 'Uncategorised';
      map[cat] = (map[cat] || 0) + 1;
    }
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [suggestions]);

  const proposalStats = useMemo(() => ({
    pending:          suggestions.filter(s => s.ai_proposal_status === 'pending').length,
    approved:         suggestions.filter(s => s.ai_proposal_status === 'approved').length,
    awaiting_testing: suggestions.filter(s => s.ai_proposal_status === 'awaiting_testing').length,
    implemented:      suggestions.filter(s => s.ai_proposal_status === 'implemented').length,
  }), [suggestions]);

  return (
    <div className="fade-in">
      {/* Page header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-extrabold text-navy">Improvement Suggestions</h2>
          <p className="text-[9px] text-gray-400 mt-0.5">{readOnly ? 'View suggestions raised by the team and how they were resolved' : 'Review and progress ideas raised by the team'}</p>
        </div>
        <div className="flex items-center gap-2 text-[8px] text-gray-400 flex-wrap justify-end">
          {Object.entries(counts).map(([s, n]) => (
            <span key={s} className={`px-2 py-0.5 rounded-full border font-bold ${STATUS_CLS[s] || ''}`}>
              {n} {STATUS_OPTIONS.find(o => o.value === s)?.label || s}
            </span>
          ))}
        </div>
      </div>

      {/* Pending proposals alert */}
      {pendingProposals.length > 0 && viewMode === 'list' && (
        <div onClick={() => setViewMode('proposals')}
          className="flex items-center gap-3 mb-4 p-3 bg-violet-50 border border-violet-200 rounded-xl cursor-pointer hover:bg-violet-100 transition-colors">
          <div className="w-2 h-2 rounded-full bg-violet-500 animate-pulse" />
          <div className="flex-1">
            <span className="text-[9px] font-bold text-violet-800">
              {pendingProposals.length} pending proposal{pendingProposals.length !== 1 ? 's' : ''} awaiting review
            </span>
            <span className="text-[8px] text-violet-500 ml-1">— click to review</span>
          </div>
          <span className="text-[8px] text-violet-500">→</span>
        </div>
      )}

      {/* Stale approved alert */}
      {approvedItems.some(s => (daysAgo(s.ai_reviewed_at) || 0) > 7) && viewMode !== 'proposals' && (
        <div onClick={() => setViewMode('proposals')}
          className="flex items-center gap-3 mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl cursor-pointer hover:bg-amber-100 transition-colors">
          <div className="w-2 h-2 rounded-full bg-amber-500" />
          <div className="flex-1">
            <span className="text-[9px] font-bold text-amber-800">
              Some approved proposals have been waiting &gt;7 days for implementation
            </span>
            <span className="text-[8px] text-amber-500 ml-1">— click to review</span>
          </div>
          <span className="text-[8px] text-amber-500">→</span>
        </div>
      )}

      {/* View / filter bar */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="flex gap-1">
          {[
            { id: 'list',      label: 'List' },
            { id: 'proposals', label: 'Proposals', badge: pendingProposals.length },
            { id: 'stats',     label: 'Stats' },
          ].map(v => (
            <button key={v.id} onClick={() => setViewMode(v.id)}
              className={`px-3 py-1 rounded text-[9px] font-semibold border transition-colors flex items-center gap-1 ${
                viewMode === v.id
                  ? v.id === 'proposals' ? 'bg-violet-600 text-white border-violet-600' : 'bg-navy text-white border-navy'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
              }`}>
              {v.label}
              {v.badge > 0 && (
                <span className={`rounded-full px-1.5 py-0 text-[7px] font-bold ${
                  viewMode === v.id ? 'bg-white/30 text-white' : 'bg-violet-100 text-violet-700'
                }`}>{v.badge}</span>
              )}
            </button>
          ))}
        </div>

        {viewMode === 'list' && (
          <div className="flex gap-1 ml-2 border-l border-gray-200 pl-2">
            {STATUS_OPTIONS.map(o => (
              <button key={o.value} onClick={() => setStatusFilter(o.value)}
                className={`px-3 py-1 rounded text-[9px] font-semibold border transition-colors ${
                  statusFilter === o.value ? 'bg-navy text-white border-navy' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                }`}>
                {o.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {loading && <div className="text-[9px] text-gray-400 py-8 text-center">Loading…</div>}

      {/* ── Proposals view ── */}
      {!loading && viewMode === 'proposals' && (
        <div>
          {/* Proposal pipeline stats */}
          <div className="grid grid-cols-4 gap-2 mb-4">
            {[
              { label: 'Pending Review',     value: proposalStats.pending,          cls: 'bg-violet-50 border-violet-200 text-violet-700' },
              { label: 'Awaiting Impl.',     value: proposalStats.approved,         cls: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
              { label: 'Awaiting Testing',   value: proposalStats.awaiting_testing, cls: 'bg-amber-50 border-amber-200 text-amber-700' },
              { label: 'Implemented',        value: proposalStats.implemented,      cls: 'bg-indigo-50 border-indigo-200 text-indigo-700' },
            ].map(stat => (
              <div key={stat.label} className={`border rounded-lg p-2.5 ${stat.cls}`}>
                <div className="text-[18px] font-extrabold leading-none">{stat.value}</div>
                <div className="text-[7px] font-semibold mt-0.5 opacity-80">{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Section: Pending Review */}
          {pendingBatches.length > 0 && (
            <div className="mb-5">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-1.5 h-1.5 rounded-full bg-violet-500" />
                <div className="text-[9px] font-bold text-gray-700 uppercase tracking-wide">Pending Review</div>
              </div>
              {pendingBatches.map(b => (
                <BatchCard key={b.batchId} batchId={b.batchId} batchLabel={b.batchLabel}
                  items={b.items} onAction={handleBatchAction} actioning={actioning} readOnly={readOnly} />
              ))}
            </div>
          )}

          {/* Section: Approved — Awaiting Implementation */}
          {approvedItems.length > 0 && (
            <div className="mb-5">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                <div className="text-[9px] font-bold text-gray-700 uppercase tracking-wide">Approved — Awaiting Implementation</div>
              </div>
              <div className="mb-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg text-[8px] text-emerald-700">
                Ask Claude Code in chat to implement these. It will update the status and notify the initiator automatically when done.
              </div>
              <div className="space-y-2">
                {approvedItems.map(s => {
                  const staleDays = daysAgo(s.ai_reviewed_at);
                  const isStale = staleDays !== null && staleDays > 7;
                  return (
                    <div key={s.id} className={`bg-white border rounded-xl p-3.5 ${isStale ? 'border-amber-300' : 'border-emerald-200'}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <div className="text-[9px] font-semibold text-gray-800">{s.title}</div>
                          <PriorityBadge priority={s.priority} />
                          {isStale && (
                            <span className="text-[7px] bg-amber-100 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded-full font-bold">
                              {staleDays}d waiting
                            </span>
                          )}
                        </div>
                        <div className="text-[8px] text-gray-400 mb-1.5">By {s.raised_by_name} · approved {fmtDateOnly(s.ai_reviewed_at)}</div>
                        <div className="text-[8px] text-gray-500 leading-relaxed mb-2">{s.description}</div>
                        {s.ai_proposal && (
                          <div className="bg-emerald-50 rounded-lg p-2 border border-emerald-100">
                            <div className="text-[7px] font-bold uppercase tracking-wide text-emerald-600 mb-0.5">Approved Proposal</div>
                            <p className="text-[8px] text-gray-700 leading-relaxed whitespace-pre-wrap">{s.ai_proposal}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Section: Awaiting User Testing */}
          {awaitingItems.length > 0 && (
            <div className="mb-5">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                <div className="text-[9px] font-bold text-gray-700 uppercase tracking-wide">Awaiting User Testing</div>
                <span className="text-[7px] text-gray-400">Initiator has been emailed to confirm</span>
              </div>
              <div className="space-y-2">
                {awaitingItems.map(s => {
                  const staleDays = daysAgo(s.ai_reviewed_at);
                  const isStale = staleDays !== null && staleDays > 3;
                  return (
                    <div key={s.id} className={`bg-white border rounded-xl p-3.5 ${isStale ? 'border-amber-300' : 'border-amber-200'}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <div className="text-[9px] font-semibold text-gray-800">{s.title}</div>
                            <AiStatusBadge status="awaiting_testing" />
                            {isStale && (
                              <span className="text-[7px] bg-amber-100 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded-full font-bold">
                                {staleDays}d no response
                              </span>
                            )}
                          </div>
                          <div className="text-[8px] text-gray-400 mb-1.5">
                            By {s.raised_by_name} · implemented {fmtDateOnly(s.ai_reviewed_at)}
                          </div>
                          {s.ai_implemented_notes && (
                            <div className="bg-amber-50 rounded-lg p-2 border border-amber-100 mb-2">
                              <div className="text-[7px] font-bold uppercase tracking-wide text-amber-600 mb-0.5">Implementation Notes</div>
                              <p className="text-[8px] text-gray-700 leading-relaxed whitespace-pre-wrap">{s.ai_implemented_notes}</p>
                            </div>
                          )}
                          {s.admin_notes && (
                            <div className="text-[7px] text-blue-600 mt-1">↳ {s.admin_notes}</div>
                          )}
                        </div>
                        <button onClick={() => setEditing(s)}
                          className="shrink-0 px-2 py-1 text-[8px] text-gray-500 hover:text-gray-700 border border-gray-200 rounded">
                          Edit
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Section: Recently Implemented */}
          {implementedItems.length > 0 && (
            <div className="mb-5">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
                <div className="text-[9px] font-bold text-gray-700 uppercase tracking-wide">Recently Implemented</div>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100">
                {implementedItems.map(s => (
                  <div key={s.id} className="px-4 py-2.5 flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <span className="text-[9px] font-semibold text-gray-700">{s.title}</span>
                      <span className="text-[7px] text-gray-400 ml-2">by {s.raised_by_name}</span>
                    </div>
                    <span className="text-[7px] text-gray-400">{fmtDateOnly(s.updated_at)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {pendingBatches.length === 0 && approvedItems.length === 0 && awaitingItems.length === 0 && implementedItems.length === 0 && (
            <div className="text-center py-12 text-[9px] text-gray-400">No proposals yet.</div>
          )}
        </div>
      )}

      {/* ── List view ── */}
      {!loading && viewMode === 'list' && (
        <>
          {filtered.length === 0 ? (
            <div className="text-center py-12 text-[9px] text-gray-400">No suggestions found.</div>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
              <table className="w-full text-[9px]">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-2 text-left text-[7px] font-bold uppercase tracking-wide text-gray-400">Title</th>
                    <th className="px-3 py-2 text-left text-[7px] font-bold uppercase tracking-wide text-gray-400">Raised By</th>
                    <th className="px-3 py-2 text-left text-[7px] font-bold uppercase tracking-wide text-gray-400">Category</th>
                    <th className="px-3 py-2 text-left text-[7px] font-bold uppercase tracking-wide text-gray-400">Priority</th>
                    <th className="px-3 py-2 text-left text-[7px] font-bold uppercase tracking-wide text-gray-400">Status</th>
                    <th className="px-3 py-2 text-left text-[7px] font-bold uppercase tracking-wide text-gray-400">Proposal</th>
                    <th className="px-3 py-2 text-left text-[7px] font-bold uppercase tracking-wide text-gray-400">Date</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.map(s => (
                    <tr key={s.id} className={suggestionStatusTint(s.status)}>
                      <td className="px-3 py-2 max-w-[200px]">
                        <div className="font-semibold text-gray-900 truncate">{s.title}</div>
                        {s.admin_notes && (
                          <div className="text-[7px] text-blue-600 mt-0.5 truncate">↳ {s.admin_notes}</div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-600">{s.raised_by_name}</td>
                      <td className="px-3 py-2 text-gray-400">{s.category || '—'}</td>
                      <td className="px-3 py-2"><PriorityBadge priority={s.priority} /></td>
                      <td className="px-3 py-2"><StatusBadge status={s.status} /></td>
                      <td className="px-3 py-2"><AiStatusBadge status={s.ai_proposal_status} /></td>
                      <td className="px-3 py-2 text-gray-400">{fmtDateOnly(s.created_at)}</td>
                      <td className="px-3 py-2 text-right">
                        <button onClick={() => setEditing(s)}
                          className={`font-semibold text-[8px] ${
                            s.ai_proposal_status === 'pending'
                              ? 'text-violet-600 hover:text-violet-800'
                              : 'text-blue-500 hover:text-blue-700'
                          }`}>
                          {readOnly ? 'View' : s.ai_proposal_status === 'pending' ? 'Review' : 'Edit'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ── Stats view ── */}
      {!loading && viewMode === 'stats' && (
        <div className="space-y-5">
          {/* By requester */}
          <div>
            <div className="text-[9px] font-bold text-gray-700 uppercase tracking-wide mb-2">By Requester</div>
            <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto">
              <table className="w-full text-[9px]">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-2 text-left text-[7px] font-bold uppercase tracking-wide text-gray-400">Name</th>
                    <th className="px-3 py-2 text-center text-[7px] font-bold uppercase tracking-wide text-amber-500">Open</th>
                    <th className="px-3 py-2 text-center text-[7px] font-bold uppercase tracking-wide text-blue-500">In Progress</th>
                    <th className="px-3 py-2 text-center text-[7px] font-bold uppercase tracking-wide text-green-600">Done</th>
                    <th className="px-3 py-2 text-center text-[7px] font-bold uppercase tracking-wide text-red-500">Declined</th>
                    <th className="px-3 py-2 text-center text-[7px] font-bold uppercase tracking-wide text-gray-400">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {requesterStats.map(r => (
                    <tr key={r.name} className="hover:bg-gray-50/60">
                      <td className="px-3 py-2 font-semibold text-gray-800">{r.name}</td>
                      <td className="px-3 py-2 text-center text-amber-700">{r.open || 0}</td>
                      <td className="px-3 py-2 text-center text-blue-700">{r['in-progress'] || 0}</td>
                      <td className="px-3 py-2 text-center text-green-700">{r.done || 0}</td>
                      <td className="px-3 py-2 text-center text-red-600">{r.declined || 0}</td>
                      <td className="px-3 py-2 text-center font-bold text-gray-700">{r.total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* By category */}
          <div>
            <div className="text-[9px] font-bold text-gray-700 uppercase tracking-wide mb-2">By Category</div>
            <div className="bg-white border border-gray-200 rounded-xl p-3 space-y-2">
              {categoryStats.map(([cat, count]) => {
                const pct = Math.round((count / suggestions.length) * 100);
                return (
                  <div key={cat} className="flex items-center gap-3">
                    <div className="w-32 text-[8px] text-gray-600 truncate">{cat}</div>
                    <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-navy rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="text-[8px] text-gray-500 w-8 text-right">{count}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Proposal pipeline totals */}
          <div>
            <div className="text-[9px] font-bold text-gray-700 uppercase tracking-wide mb-2">Proposal Pipeline</div>
            <div className="grid grid-cols-4 gap-2">
              {[
                { label: 'Pending Review',   value: proposalStats.pending,          cls: 'bg-violet-50 border-violet-200 text-violet-700' },
                { label: 'Awaiting Impl.',   value: proposalStats.approved,         cls: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
                { label: 'Awaiting Testing', value: proposalStats.awaiting_testing, cls: 'bg-amber-50 border-amber-200 text-amber-700' },
                { label: 'Implemented',      value: proposalStats.implemented,      cls: 'bg-indigo-50 border-indigo-200 text-indigo-700' },
              ].map(stat => (
                <div key={stat.label} className={`border rounded-lg p-3 ${stat.cls}`}>
                  <div className="text-[22px] font-extrabold leading-none">{stat.value}</div>
                  <div className="text-[7px] font-semibold mt-1 opacity-80">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {editing && (
        <DetailDrawer
          suggestion={editing}
          readOnly={readOnly}
          onClose={() => setEditing(null)}
          onSaved={(updated) => { handleSaved(updated); setEditing(null); }}
          onUpdated={handleSaved}
        />
      )}

    </div>
  );
}
